import {
	GridContainer,
	PageContainer,
	PageTop,
	SearchContainer,
	StyledDataGrid,
} from 'components/ApplicationStyles'
import PagePosition from 'components/PagePosition'
import { TextField, Grid, Alert, Box, Button } from '@mui/material'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { koKR } from '@mui/x-data-grid-premium/locales'
import {
	apiShopSellerAuth,
	apiShopSellerInfoBySellerIdAndUserId,
	apiUserInfoUpdateByAdmin,
	apiUserList,
} from 'api/api'
import {
	GridColDef,
	GridEventListener,
	GridRowEditStopReasons,
	GridRowModel,
	GridRowSelectionModel,
	useGridApiRef,
} from '@mui/x-data-grid-premium'
import { useQuery } from '@tanstack/react-query'
import {
	AdminMemberInfoType,
	UserInfoType,
	UserSearchType,
} from 'constants/types'
import { alertModal, confirmAlert, timerAlert } from 'util/util'
import {
	StatusType,
	SellerInfoType,
	DocumentsType,
	SellerAuthRequestType,
} from 'constants/appType'
import ShopUserInfo from './ShopUserInfo'

// 판매자 승인 처리

// React Query 키 팩토리
const userKeys = {
	all: ['user'] as const,
	lists: () => [...userKeys.all, 'list'] as const,
	list: (params: UserSearchType) => [...userKeys.lists(), params] as const,
}

const User = () => {
	// 편집된 행 데이터를 저장할 상태
	const [editedRows, setEditedRows] = useState<Map<number, UserInfoType>>(
		new Map(),
	)

	// 행 편집이 완료되었을 때 호출되는 함수
	const processRowUpdate = (newRow: GridRowModel) => {
		console.log('processRowUpdate - newRow:', newRow)
		const updatedRow = { ...(newRow as UserInfoType), isNew: false }
		console.log('processRowUpdate - updatedRow:', updatedRow)

		// 편집된 행을 Map에 저장
		setEditedRows((prev) => {
			const newMap = new Map(prev)
			newMap.set(updatedRow.id, updatedRow)
			console.log('편집된 행 Map 업데이트:', newMap)
			return newMap
		})

		return updatedRow
	}

	// 행 편집이 중지될 때 호출되는 함수
	const handleRowEditStop: GridEventListener<'rowEditStop'> = (
		params,
		event,
	) => {
		if (params.reason === GridRowEditStopReasons.rowFocusOut) {
			event.defaultMuiPrevented = true
		}
	}

	const columns: GridColDef[] = useMemo(
		() => [
			{ field: 'id', headerName: 'ID', width: 90 },
			{
				field: 'username',
				headerName: '아이디',
				width: 200,
			},
			{
				field: 'email',
				headerName: '이메일',
				width: 200,
				renderCell: (params: any) => {
					if (params.value.endsWith('senior.temp')) {
						return <></>
					} else {
						return <span>{params.value}</span>
					}
				},
			},
			{
				field: 'roles',
				headerName: '권한',
				width: 180,
				editable: true,
				type: 'singleSelect',
				valueOptions: (params: any) => {
					const email = params.row?.email || ''
					if (email.endsWith('senior.temp')) {
						return [{ value: '1', label: '일반 사용자' }]
					}
					return [
						{ value: '4', label: '시스템 관리자' },
						{ value: '3', label: '관리자' },
						{ value: '2', label: '쇼핑몰 판매자' },
						{ value: '1', label: '일반 사용자' },
					]
				},
				valueGetter: (value: any) => {
					// roles 배열에서 가장 높은 권한을 찾아서 반환
					let maxRoleNumber: string = ''
					if (Array.isArray(value)) {
						value.forEach((item: any) => {
							const role = item.role
							if (role === 'ROLE_SYSTEM') {
								maxRoleNumber = '4'
							} else if (role === 'ROLE_ADMIN') {
								if (maxRoleNumber !== '4') {
									maxRoleNumber = '3'
								}
							} else if (role === 'ROLE_SELLER') {
								if (maxRoleNumber !== '4' && maxRoleNumber !== '3') {
									maxRoleNumber = '2'
								}
							} else if (role === 'ROLE_USER') {
								if (
									maxRoleNumber !== '4' &&
									maxRoleNumber !== '3' &&
									maxRoleNumber !== '2'
								) {
									maxRoleNumber = '1'
								}
							}
						})
					}
					return maxRoleNumber || '1'
				},
				valueSetter: (value: string, row: any) => {
					// 선택된 권한에 맞게 roles 배열 업데이트
					const roleId = parseInt(value)
					const newRoles = [
						{
							id: roleId,
							role:
								value === '4'
									? 'ROLE_SYSTEM'
									: value === '3'
										? 'ROLE_ADMIN'
										: value === '2'
											? 'ROLE_SELLER'
											: 'ROLE_USER',
						},
					]
					return { ...row, roles: newRoles }
				},
				renderCell: (params: any) => {
					const roleLabels: Record<string, string> = {
						'4': '시스템 관리자',
						'3': '관리자',
						'2': '쇼핑몰 판매자',
						'1': '일반 사용자',
					}
					return roleLabels[params.value] || '일반 사용자'
				},
			},
			{
				field: 'phone',
				headerName: '전화번호',
				width: 150,
			},
			{
				field: 'active',
				headerName: '계정 활성 상태',
				width: 120,
				type: 'boolean',
				editable: true,
			},
			{
				field: 'emailVerified',
				headerName: '이메일인증',
				width: 120,
				type: 'boolean',
				editable: true,
				valueGetter: (value: any, row: any) => {
					if (row?.email?.endsWith('senior.temp')) {
						return true
					}
					return value
				},
			},
			{
				field: 'shopSeller',
				headerName: '쇼핑몰 판매자 여부',
				width: 150,
				type: 'boolean',
				editable: false,
				valueGetter: (value: any, row: any) => {
					const roles = row?.roles || []
					const { shopSeller } = row
					return roles.some((role: any) => role.role === 'ROLE_SELLER' && shopSeller)
				},
			},
			{
				field: 'name',
				headerName: '이름',
				width: 150,
			},
			{
				field: 'socialLogin',
				headerName: '소셜로그인',
				width: 120,
				type: 'boolean',
			},
			{
				field: 'provider',
				headerName: '소셜제공자',
				width: 100,
				renderCell: (params: any) => {
					const provider = params.value
					switch (provider) {
						case 'GOOGLE':
							return (
								<span
									style={{
										backgroundColor: '#FFFFFF',
										color: '#757575',
										fontWeight: 'bold',
										border: '1px solid #DADCE0',
										padding: 5,
										borderRadius: 5,
									}}
								>
									구글
								</span>
							)
						case 'KAKAO':
							return (
								<span
									style={{
										backgroundColor: '#FEE500',
										color: '#000000',
										fontWeight: 'bold',
										padding: 5,
										borderRadius: 5,
									}}
								>
									카카오
								</span>
							)
						case 'NAVER':
							return (
								<span
									style={{
										backgroundColor: '#03C75A',
										color: '#FFFFFF',
										fontWeight: 'bold',
										padding: 5,
										borderRadius: 5,
									}}
								>
									네이버
								</span>
							)
						case 'APPLE':
							return (
								<span
									style={{
										backgroundColor: '#000000',
										color: '#FFFFFF',
										fontWeight: 'bold',
										padding: 5,
										borderRadius: 5,
									}}
								>
									애플
								</span>
							)
						default:
							return <span></span>
					}
				},
			},
			{
				field: 'joinDate',
				headerName: '가입일',
				width: 180,
			},
			{
				field: 'dropDate',
				headerName: '탈퇴일',
				width: 180,
			},
			{
				field: 'shopSellerBtn',
				headerName: '판매자 정보',
				width: 150,
				renderCell: (params: any) => {
					const isSeller = params.row.roles?.some(
						(role: any) => role.role === 'ROLE_SELLER',
					)
					if (isSeller) {
						return (
							<Button
								variant="contained"
								color="primary"
								size="small"
								onClick={() => {
									showSellerInfo(params.row)
								}}
							>
								판매자 정보
							</Button>
						)
					}
					return null
				},
			},
		],
		[],
	)

	const apiRef = useGridApiRef()
	const [rowSelectionModel, setRowSelectionModel] =
		useState<GridRowSelectionModel>({ type: 'include', ids: new Set() })

	const [disabledAdd] = useState(true)
	const [disabledDel] = useState(true)
	const [disabledSave, setDisabledSave] = useState(true)
	const [disabledSearch] = useState(false)
	const [disabledReset] = useState(true)
	const [disabledExcelDownload] = useState(false)
	const [hideAdd] = useState(true)
	const [hideDel] = useState(true)
	const [hideSave] = useState(false)
	const [hideSearch] = useState(false)
	const [hideReset] = useState(true)
	const [hideExcelDownload] = useState(true)

	// 검색 파라미터
	const [search, setSearch] = useState<UserSearchType>({
		username: '',
		name: '',
		email: '',
	})

	// 검색 실행 여부 상태 (수동 검색을 위해)
	const [shouldFetch, setShouldFetch] = useState(true)

	// React Query
	const {
		data = [],
		isLoading,
		isFetching,
		isError,
		error,
		refetch,
	} = useQuery({
		queryKey: userKeys.list(search),
		queryFn: async () => {
			const result = await apiUserList(search)
			console.log('User List:', result)
			return result
		},
		enabled: shouldFetch, // shouldFetch가 true일 때만 실행
		staleTime: 5 * 60 * 1000, // 5분간 캐시 유지
		gcTime: 10 * 60 * 1000, // 10분간 가비지 컬렉션 방지
	})

	// 쿼리 성공 후 shouldFetch를 false로 설정
	useEffect(() => {
		if (data && shouldFetch) {
			setShouldFetch(false)
		}
	}, [data, shouldFetch])

	// 선택된 행에 따른 저장 버튼 활성화/비활성화
	useEffect(() => {
		setDisabledSave(rowSelectionModel.ids.size === 0)
	}, [rowSelectionModel])

	// 추가
	const actionAdd = () => {}

	// 삭제
	const actionDel = () => {}

	// 저장
	const actionSave = () => {
		// rowSelectionModel에서 선택된 행의 id 값의 행 가져오기
		const selectedRowIds = rowSelectionModel.ids
			? Array.from(rowSelectionModel.ids)
			: []
		console.log('선택된 행 IDs:', selectedRowIds)

		// 편집된 데이터를 우선 사용하고, 없으면 원본 데이터 사용
		const selectedRows = selectedRowIds
			.map((id) => {
				// 편집된 행 데이터가 있으면 그것을 사용
				if (editedRows.has(id as number)) {
					return editedRows.get(id as number)!
				}
				// 없으면 원본 데이터에서 찾기
				return data.find((row: UserInfoType) => row.id === id)!
			})
			.filter(Boolean) // null/undefined 제거

		console.log('선택된 행 데이터 (편집 반영):', selectedRows)
		console.log('현재 편집된 행 Map:', editedRows)

		if (editedRows.size === 0) {
			alertModal('변경된 내용이 없습니다.', 'info', () => {})
			return
		}

		confirmAlert(
			'선택한 회원의 정보를 저장하시겠습니까?',
			() => {
				const userList: AdminMemberInfoType[] = selectedRows.map(
					(row: UserInfoType) => ({
						id: row.id,
						name: row.name,
						roles: row.roles,
						phone: row.phone,
						active: row.active,
						emailVerified: row.emailVerified,
					}),
				)
				console.log(userList)
				apiUserInfoUpdateByAdmin(userList).then((res) => {
					console.log('회원 정보 저장 결과:', res)
					timerAlert('회원 정보가 성공적으로 저장되었습니다.', 'success', () => {})
					actionSearch() // 저장 후 목록 새로고침
					setRowSelectionModel({ type: 'include', ids: new Set() })
				})
			},
			() => {},
		)
	}

	// 검색
	const actionSearch = useCallback(() => {
		setShouldFetch(true)
		refetch()
	}, [refetch])

	// 초기화
	const actionReset = () => {}

	// 키보드 엔터키 처리
	const onKeyUp = (e: React.KeyboardEvent<HTMLFormElement>) => {
		if (e.code === 'Enter') {
			actionSearch()
		}
	}

	useEffect(() => {
		const timeoutId = setTimeout(() => {
			if (search.email !== '' || search.name !== '' || search.username !== '') {
				setShouldFetch(true)
			}
		}, 500) // 500ms debounce

		return () => clearTimeout(timeoutId)
	}, [search.email, search.name, search.username])

	const [selectedSellerInfo, setSelectedSellerInfo] =
		useState<SellerInfoType | null>(null)
	const [selectedSellerDocuments, setSelectedSellerDocuments] = useState<
		DocumentsType[] | null
	>(null)
	const [isSellerInfoOpen, setIsSellerInfoOpen] = useState(false)

	// 판매자 정보 표시
	const showSellerInfo = (row: any) => {
		const { id, sellerId } = row
		if (id && sellerId) {
			apiShopSellerInfoBySellerIdAndUserId(sellerId, id).then((res) => {
				console.log(res)
				setSelectedSellerInfo(res.sellerInfo)
				setSelectedSellerDocuments(res.documents)
				setIsSellerInfoOpen(true)
			})
		}
	}

	const shopSellerUpdate = (
		commissionRate: number,
		status: keyof StatusType,
		reason: string,
	) => {
		console.log('판매자 업데이트', {
			userId: selectedSellerInfo?.userId,
			sellerId: selectedSellerInfo?.sellerId,
			commissionRate,
			status,
			reason,
		})
		const request: SellerAuthRequestType = {
			userId: selectedSellerInfo?.userId!,
			sellerId: selectedSellerInfo?.sellerId!,
			commissionRate,
			status,
			reason: status === 'REJECTED' || status === 'SUSPENDED' ? reason : '',
		}
		apiShopSellerAuth(request).then((res) => {
			console.log('판매자 승인 처리 결과:', res)
			timerAlert('판매자 정보가 성공적으로 저장되었습니다.', 'success', () => {
				setIsSellerInfoOpen(false)
				setSelectedSellerInfo(null)
				setSelectedSellerDocuments(null)
				actionSearch() // 저장 후 목록 새로고침
			})
		})
	}

	return (
		<PageContainer>
			<ShopUserInfo
				isOpen={isSellerInfoOpen}
				onClose={() => setIsSellerInfoOpen(false)}
				sellerInfo={selectedSellerInfo}
				sellerDocuments={selectedSellerDocuments || []}
				save={(commissionRate, status, reason) =>
					shopSellerUpdate(commissionRate, status, reason)
				}
			/>
			<PageTop>
				<PagePosition
					actionAdd={actionAdd}
					actionDel={actionDel}
					actionSave={actionSave}
					actionSearch={actionSearch}
					actionReset={actionReset}
					actionExcelDownload={() => {}}
					disabledAdd={disabledAdd}
					disabledDel={disabledDel}
					disabledSave={disabledSave}
					disabledSearch={disabledSearch}
					disabledReset={disabledReset}
					disabledExcelDownload={disabledExcelDownload}
					hideAdd={hideAdd}
					hideDel={hideDel}
					hideSave={hideSave}
					hideSearch={hideSearch}
					hideReset={hideReset}
					hideExcelDownload={hideExcelDownload}
				/>
				<SearchContainer onKeyUp={onKeyUp}>
					<Grid
						container
						spacing={2}
					>
						<Grid size={6}>
							<TextField
								label="아이디"
								variant="outlined"
								fullWidth
								onChange={(e) => {
									setSearch((prev) => ({ ...prev, username: e.target.value }))
								}}
							/>
						</Grid>
						<Grid size={6}>
							<TextField
								label="이름"
								variant="outlined"
								fullWidth
								onChange={(e) => {
									setSearch((prev) => ({ ...prev, name: e.target.value }))
								}}
							/>
						</Grid>
						{/* <Grid size={4}>
							<TextField
								label="이메일"
								variant="outlined"
								fullWidth
								onChange={(e) => {
									setSearch((prev) => ({ ...prev, email: e.target.value }))
								}}
							/>
						</Grid> */}
					</Grid>
				</SearchContainer>
			</PageTop>
			<GridContainer>
				{isError && (
					<Box sx={{ mb: 2 }}>
						<Alert severity="error">
							데이터를 불러오는 중 오류가 발생했습니다: {error?.message}
						</Alert>
					</Box>
				)}
				<StyledDataGrid
					apiRef={apiRef}
					columns={columns}
					rows={data}
					showToolbar
					loading={isFetching}
					checkboxSelection
					// disableRowSelectionOnClick
					rowSelectionModel={rowSelectionModel}
					onRowSelectionModelChange={(newRowSelectionModel) => {
						setRowSelectionModel(newRowSelectionModel)
					}}
					onRowEditStop={handleRowEditStop}
					processRowUpdate={processRowUpdate}
					isCellEditable={(params) => {
						if (
							params.field === 'emailVerified' &&
							params.row?.email?.endsWith('senior.temp')
						) {
							return false
						}
						return params.colDef.editable ?? false
					}}
					// onRowClick={onRowClick}
					localeText={koKR.components.MuiDataGrid.defaultProps.localeText}
					initialState={{
						columns: {
							columnVisibilityModel: {
								id: false,
							},
						},
					}}
				/>
			</GridContainer>
		</PageContainer>
	)
}

export default User
